/* Clean searchable vendor picker with inline "add new vendor" */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { ChevronDown, Plus, Store, Check, Loader2 } from "lucide-react";
import { errMsg } from "@/hooks/useLegacy";
import { t } from "@/i18n";

export const VendorPicker = ({ api, value, onChange, vendors = [], onCreated, onCreate, placeholder = "Vendor or who was paid…", testId = "vendor-picker", disabled, className = "", kind = "vendor", onCommit }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(value || "");
  const [hi, setHi] = useState(0);
  const [adding, setAdding] = useState(false);
  const box = useRef(null);
  const menu = useRef(null);
  const [pos, setPos] = useState(null);
  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target) && !(menu.current && menu.current.contains(e.target))) { setOpen(false); setQ(value || ""); } };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, [value]);
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => { const r = box.current?.getBoundingClientRect(); if (r) setPos({ top: r.bottom + 6, left: r.left, width: r.width, up: window.innerHeight - r.bottom < 300 && r.top > 300, bottom: window.innerHeight - r.top + 6 }); };
    place(); window.addEventListener("resize", place); window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    const all = vendors.filter((v) => !s || v.toLowerCase().includes(s));
    return all.slice(0, 8);
  }, [vendors, q]);
  const exact = vendors.some((v) => v.toLowerCase() === q.trim().toLowerCase());
  const canAdd = q.trim().length > 1 && !exact;
  const options = canAdd ? [...list, "__add__"] : list;
  const pick = (v) => { onChange(v); setQ(v); setOpen(false); onCommit?.(v); };
  const add = async () => {
    const name = q.trim();
    setAdding(true);
    try {
      if (onCreate) await onCreate(name);
      else await api.post("/vendors", { Vendor_Name: name, Vendor_Type_ID: 1, Is_Active: 1, category_ids: [] });
      toast.success(`Vendor "${name}" added`);
      onCreated?.(name); pick(name);
    } catch (e) {
      if (/already exists/i.test(errMsg(e))) pick(name); else toast.error(errMsg(e));
    } finally { setAdding(false); }
  };
  const choose = (o) => (o === "__add__" ? add() : pick(o));
  const onKey = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { setOpen(true); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, options.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (options[hi] !== undefined) choose(options[hi]); else if (q.trim()) pick(q.trim()); }
    else if (e.key === "Escape") { setOpen(false); }
  };
  return (
    <div ref={box} className={`relative ${className}`} data-testid={testId}>
      <div className={`flex items-center rounded-xl border bg-white transition-colors ${open ? "border-[#663399] ring-2 ring-[#663399]/15" : "border-gray-300 hover:border-gray-400 shadow-[inset_0_1px_2px_rgba(16,24,40,0.06)]"}`}>
        <Store className="w-4 h-4 text-gray-400 ml-3 shrink-0" />
        <input data-testid={`${testId}-input`} disabled={disabled} value={q} placeholder={placeholder} autoComplete="off"
          onChange={(e) => { setQ(e.target.value); onChange(e.target.value); setOpen(true); setHi(0); }} onFocus={() => setOpen(true)} onKeyDown={onKey} onBlur={() => setTimeout(() => onCommit?.(q), 150)}
          className="flex-1 min-w-0 bg-transparent px-2.5 py-2.5 text-sm outline-none placeholder:text-gray-400" />
        <button type="button" tabIndex={-1} onClick={() => setOpen((o) => !o)} className="px-2.5 text-gray-400 hover:text-[#663399]" data-testid={`${testId}-toggle`}><ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} /></button>
      </div>
      {open && !disabled && pos && (options.length > 0 || !vendors.length) && createPortal(
        <div ref={menu} style={pos.up ? { position: "fixed", bottom: pos.bottom, left: pos.left, width: pos.width } : { position: "fixed", top: pos.top, left: pos.left, width: pos.width }} className="z-[130] overflow-hidden rounded-xl border border-gray-100 bg-white shadow-[0_12px_40px_-12px_rgba(16,24,40,0.3)]" data-testid={`${testId}-menu`}>
          {list.length > 0 && <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">{q.trim() ? t("Matches") : t("Your vendors")}</div>}
          <ul className="max-h-64 overflow-y-auto py-1">
            {list.map((v, i) => (
              <li key={v}><button type="button" data-testid={`${testId}-option-${i}`} onMouseEnter={() => setHi(i)} onClick={() => pick(v)}
                className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm ${hi === i ? "bg-[#663399]/[0.07] text-[#663399]" : "text-gray-800"}`}>
                <span className="truncate">{v}</span>{value === v && <Check className="w-4 h-4 shrink-0" />}</button></li>
            ))}
            {!vendors.length && !canAdd && <li className="px-3 py-2 text-xs text-gray-400">{kind === "customer" ? t("No customers yet – type a name to add one.") : t("No vendors yet – type a name to add one.")}</li>}
          </ul>
          {canAdd && (
            <button type="button" data-testid={`${testId}-add`} disabled={adding} onMouseEnter={() => setHi(options.length - 1)} onClick={add}
              className={`flex w-full items-center gap-2 border-t border-gray-100 px-3 py-2.5 text-left text-sm font-semibold ${hi === options.length - 1 ? "bg-emerald-50 text-emerald-700" : "text-emerald-700"}`}>
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}{t("Add")} “{q.trim()}” {kind === "customer" ? t("as a new customer") : t("as a new vendor")}
            </button>
          )}
        </div>, document.body)}
    </div>
  );
};
